import { z } from 'zod';

/**
 * Wave 4 enums: AI drafts, usage and budget (SPEC §8, PLAN §3.2 "AI: drafts, usage and budget",
 * D-025, D-061, D-870 ff.). The CHECK constraints of `ai_usage` and `ai_drafts`
 * (packages/db/src/schema/ai.ts) are built from these lists.
 */

/** `ai_usage.feature` and `ai_drafts.feature`. */
export const AI_FEATURES = [
  'message_to_task',
  'transcript_extract',
  'thread_summary',
  'channel_catchup',
  'daily_report_summary',
  'weekly_review_narrative',
  'appraisal_comment',
] as const;
export type AiFeature = (typeof AI_FEATURES)[number];
export const aiFeatureSchema = z.enum(AI_FEATURES);

/** `ai_usage.status`. */
export const AI_USAGE_STATUSES = [
  'reserved',
  'succeeded',
  'failed',
  'invalid_output',
  'blocked_budget',
  'disabled',
] as const;
export type AiUsageStatus = (typeof AI_USAGE_STATUSES)[number];
export const aiUsageStatusSchema = z.enum(AI_USAGE_STATUSES);

/** `ai_drafts.draft_type`. */
export const AI_DRAFT_TYPES = [
  'task',
  'decision_request',
  'meeting_decision',
  'meeting_notes',
  'summary',
  'narrative',
  'appraisal_comment',
] as const;
export type AiDraftType = (typeof AI_DRAFT_TYPES)[number];
export const aiDraftTypeSchema = z.enum(AI_DRAFT_TYPES);

/** `ai_drafts.status`: `approved` is unchanged, `edited` is confirmed with changes. */
export const AI_DRAFT_STATUSES = [
  'pending',
  'approved',
  'edited',
  'discarded',
  'expired',
  'superseded',
] as const;
export type AiDraftStatus = (typeof AI_DRAFT_STATUSES)[number];
export const aiDraftStatusSchema = z.enum(AI_DRAFT_STATUSES);

/** `ai_drafts.result_type`: the object an approved draft created. */
export const AI_DRAFT_RESULT_TYPES = [
  'task',
  'meeting_decision',
  'meeting_note',
  'message',
  'weekly_review_narrative',
  'appraisal_comment',
] as const;
export type AiDraftResultType = (typeof AI_DRAFT_RESULT_TYPES)[number];
export const aiDraftResultTypeSchema = z.enum(AI_DRAFT_RESULT_TYPES);

/** Hours a pending draft lives: 14 days for extraction and narrative drafts, 24 hours for summaries. */
export const AI_DRAFT_TTL_HOURS: Readonly<Record<AiFeature, number>> = Object.freeze({
  message_to_task: 24,
  transcript_extract: 14 * 24,
  thread_summary: 24,
  channel_catchup: 24,
  daily_report_summary: 24,
  weekly_review_narrative: 14 * 24,
  appraisal_comment: 14 * 24,
});

/** Defaults of `ai_budgets` (PLAN): ₹5,000 a month, warn at 80%. */
export const AI_BUDGET_DEFAULTS = Object.freeze({
  monthlyCapPaise: 500_000,
  warnAtPct: 80,
});
